import { useEffect, useState } from 'react'
import {
	Toggle,
	AppButton,
	Tooltip,
	Modal,
	ModalHeader
} from '@clickguard/design-system'
import { AdAccountDTO } from 'api-models'
import { AdAccounts } from 'services/user'
import { useAssetStore } from 'store'
import { useShallow } from 'zustand/react/shallow'
import { FiHelpCircle } from 'react-icons/fi'
import { CopyButton } from 'components/copy-button/copy-button'
import { ConnectionSection, ConnectionSectionTitle } from './connections-sheet'

type VerifyResult = {
	verified: boolean
	trackingTemplateMatch: boolean
	urlSuffixMatch: boolean
	currentTrackingTemplate: string | null
	currentUrlSuffix: string | null
	expectedSuffix: string | null
	expectedTemplate: string | null
}

/**
 * Ad Click Tracking Setup card (DEV-2352 §2.2 card 2 + §4) — Google/Microsoft
 * only. Live-fetches the account-level Tracking Template + Final URL suffix on
 * open (and on toggle change) via /verify-tracking, shows them read-only, and
 * exposes the binary "Use ClickGuard's tracking template" toggle.
 *
 * CTA logic (interim): a platform write is needed whenever the desired mode
 * isn't already satisfied (`!verified`) → "Re-sync" behind a confirmation
 * modal; otherwise the change is flag-only → "Save changes" (no modal).
 *
 * ⚠️ The exact modal-gating (Figma scenarios 2 & 8 — whether the modal is gated
 * PURELY on a URL-suffix write, so a TT-only write skips it) is pending the
 * designer's decision. This implementation shows the modal for any platform
 * write (the ticket-text behaviour); revisit once confirmed.
 */
export const TrackingSetupCard = ({
	adAccount,
	accountId,
	assetId,
	open,
	canWrite,
	onClose
}: {
	adAccount: AdAccountDTO
	accountId?: string
	assetId?: string
	open: boolean
	canWrite: boolean
	onClose: () => void
}) => {
	const { updateTrackingToggles, refreshAdAccounts } = useAssetStore(
		useShallow((s) => ({
			updateTrackingToggles: s.updateTrackingToggles,
			refreshAdAccounts: s.refreshAdAccounts
		}))
	)

	const initialToggle = !!(adAccount as any).preferences?.set_tracking_template
	const [useCgTemplate, setUseCgTemplate] = useState(initialToggle)
	const [result, setResult] = useState<VerifyResult | null>(null)
	const [isError, setIsError] = useState(false)
	const [loading, setLoading] = useState(false)
	const [saving, setSaving] = useState(false)
	const [confirmResync, setConfirmResync] = useState(false)

	// Verifies the platform against the account's SAVED tracking mode
	// (the backend derives 'both' vs 'suffix-only' from
	// `preferences.set_tracking_template`), so it takes no toggle argument.
	const fetchVerify = async () => {
		if (!accountId || !assetId) return
		setLoading(true)
		setIsError(false)
		try {
			const r = await AdAccounts.verifyTracking(
				accountId,
				assetId,
				adAccount.id
			)
			setResult(r as VerifyResult)
		} catch {
			setResult(null)
			setIsError(true)
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		if (open) {
			setUseCgTemplate(initialToggle)
			fetchVerify()
		}
	}, [open, adAccount.id])

	const onToggle = (v: boolean) => {
		setUseCgTemplate(v)
	}

	const dirty = useCgTemplate !== initialToggle
	// A mode change always needs a platform write; when the mode is unchanged we
	// still re-sync if the platform has drifted from the saved configuration.
	const needsWrite = dirty || (result ? !result.verified : false)
	const showCta = dirty || needsWrite
	const ctaLabel = 'Save changes'
	const showTTError = result?.trackingTemplateMatch === false && useCgTemplate

	// Determine tracking template helper text
	const ttHelperText =
		dirty && useCgTemplate
			? "Click ”Re-sync” to replace your tracking template with ClickGuard's"
			: result?.trackingTemplateMatch || loading
			? ''
			: "You are not currently using ClickGuard's template."

	const saveFlags = async () => {
		if (!accountId) return
		await updateTrackingToggles(accountId, adAccount.id, adAccount.platform, {
			set_tracking_template: useCgTemplate,
			automated_tracking: true
		})
	}

	const onCta = async () => {
		if (!result?.trackingTemplateMatch && useCgTemplate) {
			setConfirmResync(true)
			return
		}
		await doResync()
	}

	const doResync = async () => {
		if (!accountId) return
		setSaving(true)
		try {
			await saveFlags()
			await AdAccounts.resyncTracking(accountId, adAccount.id)
			await refreshAdAccounts()
			onClose()
		} catch {
			// Error toast already surfaced by the store; keep the drawer open so
			// the user can retry instead of silently closing as if it saved.
		} finally {
			setSaving(false)
			setConfirmResync(false)
		}
	}

	if (isError) {
		return (
			<ConnectionSection>
				<ConnectionSectionTitle title="Ad click tracking setup" />
				<p
					role="status"
					className="rounded-lg border border-[rgb(var(--cg-warning-dark))] bg-[rgb(var(--cg-warning-50))] p-3 text-[13px] leading-[18px] text-[rgb(var(--cg-text-primary))]"
				>
					Access lost. Please reconnect.
				</p>
			</ConnectionSection>
		)
	}

	return (
		<ConnectionSection>
			<ConnectionSectionTitle
				title="Ad click tracking setup"
				hint="These are your current account level tracking settings."
			/>
			<div className="flex flex-col gap-4">
				<div className="flex flex-col gap-[8px]">
					<div className="flex items-center gap-[8px]">
						<span className="text-base font-medium text-[rgb(var(--cg-text-primary))]">
							Tracking template
						</span>
						<Tooltip
							position="top"
							portal
							content={
								<p>
									This is the URL that processes your ad clicks for tracking.
									<br />
									<br />
									You can keep your current template, but it may reduce click
									tracking accuracy. In that case, ClickGuard will use Final URL
									parameters to detect and analyze ad clicks.
									<br />
									<br />
									Using ClickGuard’s Tracking Template ensures the most reliable
									results. If you choose to use ours, make sure to disable any
									automated process that could overwrite it.
								</p>
							}
						>
							<FiHelpCircle className="w-[16px] h-[16px] text-[rgb(var(--cg-text-tertiary))]" />
						</Tooltip>
					</div>
					<div className="bg-bg-plan-fill rounded-[8px] px-[14px] py-[10px] flex items-center gap-[8px]">
						<p className="flex-1 text-base text-[color:var(--muted-ink,#616064)] overflow-hidden text-ellipsis whitespace-nowrap min-w-0">
							{loading
								? 'Loading…'
								: result?.currentTrackingTemplate ?? '(empty)'}
						</p>
						{!loading && !!result?.currentTrackingTemplate && (
							<CopyButton value={result.currentTrackingTemplate} />
						)}
					</div>
				</div>
				{showTTError && (
					<div className="flex flex-row items-start gap-2">
						<div className="text-[color:var(--error-ink,#F04438)]">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="11"
								height="11"
								viewBox="0 0 11 11"
								fill="none"
							>
								<path
									d="M5.5 3.5V5.5M5.5 7.5H5.505M10.5 5.5C10.5 8.26142 8.26142 10.5 5.5 10.5C2.73858 10.5 0.5 8.26142 0.5 5.5C0.5 2.73858 2.73858 0.5 5.5 0.5C8.26142 0.5 10.5 2.73858 10.5 5.5Z"
									stroke="currentColor"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</div>
						<span className="text-[11px] leading-[11px] text-[color:var(--error-ink,#F04438)]">
							ClickGuard tracking template was overwritten by another platform.
							If you want to keep using our tracking template, click the button
							below. If not, disable the toggle below to dismiss this error
							message.
						</span>
					</div>
				)}
				{!showTTError && !!ttHelperText && (
					<p className="text-[14px] leading-[18px] text-[color:var(--text-secondary)]">
						{ttHelperText}
					</p>
				)}
				<div className="flex items-center gap-[8px]">
					<span className="text-base font-medium text-[rgb(var(--cg-text-primary))]">
						Final URL suffix
					</span>
					<Tooltip
						position="top"
						portal
						content={
							<p>
								Parameters that the ad platform automatically adds to the end of
								your URL when someone clicks on your ad.
								<br />
								<br />
								Example: www.yourwebsite.com/?param1=value1&param2=value2
								<br />
								<br />
								Including ClickGuard’s parameters is mandatory for successful ad
								click tracking.
							</p>
						}
					>
						<FiHelpCircle className="w-[16px] h-[16px] text-[rgb(var(--cg-text-tertiary))]" />
					</Tooltip>
				</div>
				<div className="bg-bg-plan-fill rounded-[8px] px-[14px] py-[10px] flex items-center gap-[8px]">
					<p className="flex-1 text-base text-[color:var(--muted-ink,#616064)] break-words min-w-0">
						{loading ? 'Loading…' : result?.currentUrlSuffix ?? '(empty)'}
					</p>
					{!loading && !!result?.currentUrlSuffix && (
						<CopyButton value={result.currentUrlSuffix} />
					)}
				</div>
				{result?.urlSuffixMatch === false && (
					<div className="flex flex-row items-start gap-2">
						<div className="text-[color:var(--error-ink,#F04438)]">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="11"
								height="11"
								viewBox="0 0 11 11"
								fill="none"
							>
								<path
									d="M5.5 3.5V5.5M5.5 7.5H5.505M10.5 5.5C10.5 8.26142 8.26142 10.5 5.5 10.5C2.73858 10.5 0.5 8.26142 0.5 5.5C0.5 2.73858 2.73858 0.5 5.5 0.5C8.26142 0.5 10.5 2.73858 10.5 5.5Z"
									stroke="currentColor"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</div>
						<span className="text-[11px] leading-[11px] text-[color:var(--error-ink,#F04438)]">
							Not all ClickGuard parameters are present in your URL suffix.
							Click the button below so we can reapply and make sure your
							account is set up properly.
						</span>
					</div>
				)}
				<div className="flex min-h-[36px] flex-row items-center justify-between gap-2">
					{!loading && (
						<Toggle
							id={`tracking-template-${adAccount.id}`}
							size="md"
							label="Use ClickGuard's tracking template"
							checked={useCgTemplate}
							disabled={!canWrite}
							onChange={() => onToggle(!useCgTemplate)}
						/>
					)}

					{showCta && !loading && (
						<AppButton
							loader="dots"
							variant="primary"
							size="md"
							label={ctaLabel}
							disabled={!canWrite}
							isLoading={saving}
							onClick={onCta}
						/>
					)}
				</div>
			</div>

			<Modal open={confirmResync} onClose={() => setConfirmResync(false)}>
				<div className="cg-flex cg-flex-col cg-px-12 cg-py-8 gap-6">
					<ModalHeader
						size="md"
						titleColor="navy"
						title="Re-sync your tracking setup"
						subtitle="Review the changes below before confirming."
					/>
					<div className="cg-flex cg-flex-col gap-4">
						<div className="flex flex-col gap-3">
							<span className="text-[16px] leading-[22px] font-medium text-t-google-ad">
								Tracking template
							</span>
							<div className="bg-bg-plan-fill rounded-[8px] px-[14px] py-[10px] flex items-center gap-[8px]">
								<p className="flex-1 text-[16px] leading-[22px] text-[color:var(--muted-ink,#616064)] overflow-hidden text-ellipsis whitespace-nowrap min-w-0">
									{useCgTemplate
										? result?.expectedTemplate ?? '(empty)'
										: result?.currentTrackingTemplate ?? '(empty)'}
								</p>
							</div>
						</div>
						<div className="flex flex-col gap-3">
							<span className="text-[16px] leading-[22px] font-medium text-t-google-ad">
								Final URL suffix
							</span>
							<div className="bg-bg-plan-fill rounded-[8px] px-[14px] py-[10px] flex items-center gap-[8px]">
								<p className="flex-1 text-[16px] leading-[22px] text-[color:var(--muted-ink,#616064)] overflow-hidden text-ellipsis min-w-0">
									{result?.expectedSuffix ?? '(empty)'}
								</p>
							</div>
						</div>
					</div>
					<AppButton
						loader="dots"
						variant="primary"
						size="md"
						label="Confirm"
						disabled={!canWrite}
						isLoading={saving}
						onClick={doResync}
					/>
				</div>
			</Modal>
		</ConnectionSection>
	)
}
